import { useEffect } from 'react';
import { AnalyticsController } from 'analytics/analytics-controller';
import { ExperimentVariants } from 'utils/types';

/**
 * Props for the useTrackExperimentVariant hook
 */
type UseTrackExperimentVariantProps = {
  /** The experiment variant to track (A or B) */
  variant: ExperimentVariants;
  /** Analytics event name to track when variant is A */
  eventA: string;
  /** Analytics event name to track when variant is B */
  eventB: string;
  /** Whether the experiment is active. When false, no events will be tracked. Defaults to true */
  isActive?: boolean;
};

/**
 * A React hook for tracking experiment variant analytics events.
 *
 * This hook automatically tracks the appropriate analytics event based on the experiment variant.
 * It's designed to be used with A/B testing scenarios where you need to track different events
 * for different variants of an experiment.
 *
 * @example
 * ```tsx
 * // Basic usage
 * const experimentVariant = useFeatureValue('MY_EXPERIMENT_FLAG', ExperimentVariants.A);
 *
 * useTrackExperimentVariant({
 *   variant: experimentVariant,
 *   eventA: 'EXPERIMENT_A_CLICKED',
 *   eventB: 'EXPERIMENT_B_CLICKED',
 * });
 * ```
 *
 * @example
 * ```tsx
 * // With conditional activation
 * useTrackExperimentVariant({
 *   variant: abTestVariant,
 *   eventA: 'ONLINE_ESTIMATE_CTA_CLICKED',
 *   eventB: 'OFFLINE_ESTIMATE_CTA_CLICKED',
 *   isActive: listing.isSold, // Only track for sold listings
 * });
 * ```
 * @param props the hook configuration object
 * @param props.variant  The experiment variant (A or B)
 * @param props.eventA Analytics event to track for variant A
 * @param props.eventB Analytics event to track for variant B
 * @param props.isActive Whether the experiment is active (defaults to true)
 *
 * @remarks
 * - The hook will re-track events if any of the dependencies change (variant, eventA, eventB, isActive)
 * - When isActive is false, no events will be tracked
 * - When isActive is undefined or null, it defaults to true
 * - The hook uses useEffect internally, so it follows React's effect lifecycle
 *
 * @see {@link ExperimentVariants} for available variant options
 * @see {@link AnalyticsController} for the underlying analytics tracking
 */
export function useTrackExperimentVariant({
  variant,
  eventA,
  eventB,
  isActive = true,
}: UseTrackExperimentVariantProps): void {
  useEffect(() => {
    if (isActive === false) {
      return;
    }

    switch (variant) {
      case ExperimentVariants.A:
        AnalyticsController.trackEvent(eventA);
        break;
      case ExperimentVariants.B:
        AnalyticsController.trackEvent(eventB);
        break;
    }
  }, [variant, eventA, eventB, isActive]);
}
